import React, { useState, useCallback, useMemo, useId } from 'react';
import { X, Plus, CaretRight, CheckCircle } from '@phosphor-icons/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../queries/keys';
import { invalidateCollectionsForItems } from '../queries/collections';
import { useScrollLock } from '../hooks/useScrollLock';
import Modal from './Modal';

const PANEL_MOTION = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 40 },
};

/**
 * Collection Picker Modal
 *
 * Shared modal for adding items to user collections.
 * Used by both multiselect grid mode and single-item detail view.
 *
 * Props:
 *   items: Array of { item_id, item_type } to add
 *   onClose: callback to close modal
 *   onSuccess: callback after items are added (message)
 */
export default function CollectionPickerModal({ items, onClose, onSuccess }) {
  const titleId = useId();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [error, setError] = useState(null);

  const { targetRef: scrollRef, unlock: unlockScroll } = useScrollLock(true);

  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [unlockScroll, onClose]);

  // Shares its cache entry with MyCollections/CollectionDetail's sidebar
  // query (same key) — reopening the picker after visiting either page
  // renders the list instantly instead of refetching.
  const { data: collectionsData, isLoading: loading } = useQuery({
    queryKey: queryKeys.myCollections(user?.id),
    queryFn: () => api.getMyCollections(),
    enabled: !!user?.id,
  });
  const collections = collectionsData?.collections ?? [];

  // For a single item, check which collections it already belongs to so
  // those entries render as "already added" instead of clickable. The query
  // is disabled (via `enabled`) whenever there's no single item, so its key
  // never needs to be a real one in that case — react-query never runs it.
  const singleItem = items.length === 1 ? items[0] : null;
  const { data: membershipData } = useQuery({
    queryKey: queryKeys.collectionsForItem(user?.id, singleItem?.item_id, singleItem?.item_type),
    queryFn: () => api.getCollectionsForItem(singleItem.item_id, singleItem.item_type),
    enabled: !!user?.id && !!singleItem,
  });
  const membershipMap = useMemo(() => {
    const map = {};
    (membershipData?.collections || []).forEach(c => { map[c.id] = true; });
    return map;
  }, [membershipData]);

  const filteredCollections = useMemo(() => {
    if (!searchQuery.trim()) return collections;
    const q = searchQuery.toLowerCase();
    return collections.filter(c => c.name.toLowerCase().includes(q));
  }, [collections, searchQuery]);

  // Invalidate (not optimistic-patch) on success: the picker doesn't know
  // the target collection's up-to-date item list/preview thumbnails, so
  // refetching from the server is the only way MyCollections/CollectionDetail
  // show the new membership without a manual reload.
  const addMutation = useMutation({
    mutationFn: (collectionId) => api.addItemsToCollection(collectionId, items),
    onSuccess: (_, collectionId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myCollections(user?.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.collection(user?.id, collectionId) });
      invalidateCollectionsForItems(queryClient, user?.id, items);
      const collectionName = collections.find(c => c.id === collectionId)?.name;
      setError(null);
      onSuccess(`Added ${items.length} item${items.length === 1 ? '' : 's'} to ${collectionName || 'collection'}`);
      handleClose();
    },
    onError: () => setError('Failed to add items. Please try again.'),
  });

  const createAndAddMutation = useMutation({
    mutationFn: async ({ name, description }) => {
      const newCollection = await api.createCollection({ name, description });
      await api.addItemsToCollection(newCollection.id, items);
      return newCollection;
    },
    onSuccess: (newCollection) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.myCollections(user?.id) });
      invalidateCollectionsForItems(queryClient, user?.id, items);
      setError(null);
      onSuccess(`Added ${items.length} item${items.length === 1 ? '' : 's'} to ${newCollection.name}`);
      handleClose();
    },
    onError: () => setError('Failed to create collection. Please try again.'),
  });

  // Both handlers guard on *both* mutations' isPending, not just their own —
  // without that, submitting the create-form while an add-to-existing-
  // collection is still in flight (or vice versa) fires both concurrently.
  const anyMutationPending = addMutation.isPending || createAndAddMutation.isPending;

  const handleAddToCollection = (collectionId) => {
    if (anyMutationPending) return;
    setError(null);
    addMutation.mutate(collectionId);
  };

  const handleCreate = (e) => {
    e.preventDefault();
    if (!createName.trim() || anyMutationPending) return;
    setError(null);
    createAndAddMutation.mutate({
      name: createName.trim(),
      description: createDescription.trim() || undefined,
    });
  };

  const addingTo = addMutation.isPending ? addMutation.variables : null;
  const creating = createAndAddMutation.isPending;

  return (
    <Modal
      onClose={handleClose}
      labelledBy={titleId}
      className="collection-picker"
      panelRef={scrollRef}
      panelMotionProps={PANEL_MOTION}
    >
        <div className="collection-picker-header">
          <h2 id={titleId}>Add to Collection</h2>
          <button className="btn btn-ghost btn-icon" onClick={handleClose}>
            <CloseIcon />
          </button>
        </div>

        {error && (
          <div style={{ padding: '0 var(--space-md)', marginBottom: 'var(--space-sm)' }}>
            <div style={{ color: 'var(--danger)', fontSize: '0.8125rem', padding: 'var(--space-xs) var(--space-sm)', background: 'var(--danger-soft)', borderRadius: 'var(--radius-sm)' }}>
              {error}
            </div>
          </div>
        )}

        <div className="collection-picker-search">
          <input
            type="text"
            placeholder="Search collections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="collection-input"
          />
        </div>

        <div className="collection-picker-list">
          {/* Create new collection option */}
          {!showCreate ? (
            <button
              className="collection-picker-create-btn"
              onClick={() => setShowCreate(true)}
            >
              <PlusIcon />
              <span>Create new collection</span>
            </button>
          ) : (
            <form className="collection-picker-create-form" onSubmit={handleCreate}>
              <input
                type="text"
                placeholder="Collection name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                maxLength={100}
                autoFocus
                className="collection-input"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                className="collection-input"
              />
              <div style={{ display: 'flex', gap: 'var(--space-sm)', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setShowCreate(false); setCreateName(''); setCreateDescription(''); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={!createName.trim() || anyMutationPending}>
                  {creating ? 'Creating...' : 'Create & Add'}
                </button>
              </div>
            </form>
          )}

          {/* Collections list */}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-lg)' }}>
              <div className="loading-spinner" />
            </div>
          ) : filteredCollections.length === 0 ? (
            <div className="collection-picker-empty">
              {searchQuery ? 'No collections match your search' : 'No collections yet'}
            </div>
          ) : (
            filteredCollections.map((collection) => {
              const alreadyAdded = membershipMap[collection.id];
              return (
                <button
                  key={collection.id}
                  className={`collection-picker-item ${alreadyAdded ? 'already-added' : ''}`}
                  onClick={() => !alreadyAdded && handleAddToCollection(collection.id)}
                  disabled={anyMutationPending || alreadyAdded}
                >
                  <div className="collection-picker-item-info">
                    <span className="collection-picker-item-name">{collection.name}</span>
                    <span className="collection-picker-item-count">
                      {collection.item_count} {collection.item_count === 1 ? 'item' : 'items'}
                    </span>
                  </div>
                  <div className="collection-picker-item-thumbnails">
                    {collection.preview_thumbnails?.slice(0, 3).map((url, i) => (
                      <img key={i} src={url} alt="" className="collection-picker-thumb" />
                    ))}
                  </div>
                  {alreadyAdded ? (
                    <CheckCircleIcon />
                  ) : addingTo === collection.id ? (
                    <div className="loading-spinner loading-spinner-sm" />
                  ) : (
                    <ChevronIcon />
                  )}
                </button>
              );
            })
          )}
        </div>
    </Modal>
  );
}

function CloseIcon() {
  return <X size={20} />;
}

function PlusIcon() {
  return <Plus size={16} />;
}

function ChevronIcon() {
  return <CaretRight size={16} style={{ opacity: 0.4 }} />;
}

function CheckCircleIcon() {
  return <CheckCircle size={18} style={{ color: 'var(--accent)' }} />;
}
